import {useGoogleTranslate} from '../google-translate-api/contexts'
import useAsync from '../commerce-api/hooks/useAsync'
import {useEffect, useMemo, useRef} from 'react'
import {useIntl} from 'react-intl'

/**
 * Custom hook to translate text using Google Translate API.
 * @param {boolean} [disabled=false] - Whether the translation is disabled.
 * @param {string} text - The text to be translated.
 * @param {string} fromLanguage - The language code of the text to be translated.
 * @return {{
 *   available: boolean,
 *   displayLang: string,
 *   displayText: string,
 *   error: Error|null,
 *   loading: boolean
 * }}
 */
const useTranslation = ({disabled, text, fromLanguage}) => {
    const {locale} = useIntl()
    const currentLang = useMemo(() => locale?.split('-')[0], [locale])
    const cachedTranslation = useRef({})
    const {translate, apiEnabled} = useGoogleTranslate()
    const {execute, loading, error, data} = useAsync(
        async (text, fromLanguage, currentLang, signal) => {
            if (
                cachedTranslation.current.text === text &&
                cachedTranslation.current.fromLanguage === fromLanguage &&
                cachedTranslation.current.currentLang === currentLang
            ) {
                return cachedTranslation.current.result
            }

            const result = await translate(text, fromLanguage, currentLang, 'text', signal)

            if (!signal.aborted) {
                cachedTranslation.current = {
                    text,
                    fromLanguage,
                    currentLang,
                    result
                }
            }

            return result
        }
    )
    const translatedText = useMemo(() => data?.data?.translations?.[0]?.translatedText, [data])
    const available = Boolean(
        apiEnabled && text && fromLanguage && currentLang && fromLanguage !== currentLang
    )

    useEffect(() => {
        if (available && !disabled) {
            const abortController = new AbortController()
            execute(text, fromLanguage, currentLang, abortController.signal)
            return () => abortController.abort()
        }
    }, [available, currentLang, disabled, execute, fromLanguage, text])

    const [displayText, displayLang] = useMemo(() => {
        if (available && !disabled && !error && !loading && translatedText) {
            return [translatedText, currentLang]
        }
        return [text, fromLanguage]
    }, [available, currentLang, disabled, error, fromLanguage, loading, text, translatedText])

    return {available, displayLang, displayText, error, loading}
}

export default useTranslation
